import React, { useEffect, useState } from 'react';
import { Button, HTMLSelect, HTMLTable, InputGroup } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { InputSeries } from '../api/cppmodelApi';
import { msToSeconds } from '../utils/time';
import { insertInputPoint, removeInputPoint, setInputPoint } from './inputEditing';

interface InputEditorProps {
  inputs: InputSeries[];
  editingLabel: string | null;
  onEditingLabelChange: (label: string | null) => void;
  onInputChange: (series: InputSeries) => void;
  // Inputs that differ from the values saved for the next execution.
  changedLabels: Set<string>;
  onResetInput: (label: string) => void;
}

const NO_INPUT = '';

// A number field that keeps the typed text until it is committed (blur or
// Enter), so partial values like "1." or "-" can be typed.
const NumberCell: React.FC<{ value: number; ariaLabel: string; onCommit: (value: number) => boolean }> = ({
  value,
  ariaLabel,
  onCommit,
}) => {
  const [draft, setDraft] = useState(value.toString());

  useEffect(() => {
    setDraft(value.toString());
  }, [value]);

  const commit = () => {
    const parsed = Number(draft);

    if (draft.trim().length === 0 || Number.isNaN(parsed) || !onCommit(parsed)) {
      setDraft(value.toString());
    }
  };

  return (
    <InputGroup
      small
      value={draft}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
        }
      }}
      style={{ width: '80px' }}
    />
  );
};

// Pick an input series to edit and change its points exactly. The same series
// can be edited by dragging its points on the 2D graph.
export const InputEditor: React.FC<InputEditorProps> = ({
  inputs,
  editingLabel,
  onEditingLabelChange,
  onInputChange,
  changedLabels,
  onResetInput,
}) => {
  const series = inputs.find((input) => input.label === editingLabel);

  if (inputs.length === 0) {
    return null;
  }

  // Splitting the last interval keeps the signal unchanged until the new point
  // is edited.
  const addPoint = () => {
    if (!series) {
      return;
    }

    const count = series.x.length;

    if (count >= 2) {
      const time = (series.x[count - 2] + series.x[count - 1]) / 2;
      onInputChange(insertInputPoint(series, time, series.y[count - 2]));
    } else {
      onInputChange(insertInputPoint(series, (series.x[0] ?? 0) + 1000, series.y[0] ?? 0));
    }
  };

  return (
    <div className="cm-input-editor">
      <h2 className="cm-section-title">Simulation Inputs</h2>
      <HTMLSelect
        fill
        value={editingLabel ?? NO_INPUT}
        onChange={(e) => onEditingLabelChange(e.target.value === NO_INPUT ? null : e.target.value)}
        aria-label="Input to edit"
      >
        <option value={NO_INPUT}>Select an input to edit</option>
        {inputs.map((input) => (
          <option key={input.label} value={input.label}>
            {input.label}{changedLabels.has(input.label) ? ' (changed)' : ''}
          </option>
        ))}
      </HTMLSelect>

      {series && (
        <>
          <p className="cm-input-editor-hint">
            On the 2D graph, drag a point to move it, double-click the plot to add a
            point and double-click a point to remove it. The value holds until the next point.
          </p>
          <HTMLTable striped compact style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Time [s]</th>
                <th>Value</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {series.x.map((time, index) => (
                <tr key={`${index}-${time}`}>
                  <td>
                    <NumberCell
                      value={msToSeconds(time)}
                      ariaLabel={`Time of point ${index + 1}`}
                      onCommit={(seconds) => {
                        const next = setInputPoint(series, index, seconds * 1000, series.y[index]);
                        if (next) {
                          onInputChange(next);
                        }
                        return next !== null;
                      }}
                    />
                  </td>
                  <td>
                    <NumberCell
                      value={series.y[index]}
                      ariaLabel={`Value of point ${index + 1}`}
                      onCommit={(value) => {
                        onInputChange(setInputPoint(series, index, time, value) ?? series);
                        return true;
                      }}
                    />
                  </td>
                  <td align="right">
                    <Button
                      small
                      minimal
                      icon={IconNames.Cross}
                      aria-label={`Remove point ${index + 1}`}
                      disabled={series.x.length <= 1}
                      onClick={() => onInputChange(removeInputPoint(series, index))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
          <div className="cm-input-editor-actions">
            <Button small icon={IconNames.Plus} onClick={addPoint}>Add point</Button>
            <Button
              small
              minimal
              icon={IconNames.Reset}
              disabled={!changedLabels.has(series.label)}
              onClick={() => onResetInput(series.label)}
            >
              Reset
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
